import { ChevronDown } from '@app/components/various/Icons';
import { MenuItem } from '@app/components/various/Menu';
import { Portal } from '@app/components/various/Portal';
import { useClickOutside } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Dropdown } from '@typings';
import React from 'react';
import { mergeRefs, useLayer } from 'react-laag';

import { useDropdownContext } from '../context/DropdownContext';
import { DropdownDrawer } from '../DropdownDrawer';
import { DropdownPopover } from '../DropdownPopover';

import { DropdownMenu } from './DropdownMenu';

interface Props {
  item: Dropdown.MenuItemParent;
  isFocused?: boolean;
}

export const DropdownMenuParentItem = React.forwardRef<HTMLButtonElement, Props>(({ isFocused = false, item }, ref) => {
  const { label, menu, isDisabled, description, icon: Icon, hasDivider, onClick } = item;

  const menuId = React.useId();
  const itemRef = React.useRef<HTMLButtonElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  const { activeParentItemKey, isMobile, closeDropdown, setActiveParentItemKey } = useDropdownContext();

  const isOpen = activeParentItemKey === item.key;

  const layerProps = useLayer({
    auto: true,
    isOpen: isOpen && !isMobile,
    onParentClose: () => setActiveParentItemKey(null),
    placement: 'right-start',
    possiblePlacements: ['right-start', 'right-end', 'right-center', 'left-start', 'left-end', 'left-center'],
    preferX: 'right',
    triggerOffset: 16,
  });

  useClickOutside({
    element: itemRef.current,
    enabled: isOpen && !isMobile,
    handler: () => setActiveParentItemKey(null),
    omit: [popoverRef.current],
  });

  const openMenu = () => {
    setActiveParentItemKey(item.key);
  };

  const closeMenu = () => {
    setActiveParentItemKey(null);
    itemRef.current?.focus();
  };

  const handleClick = () => {
    isOpen ? closeMenu() : openMenu();
    onClick?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      openMenu();
    }

    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      openMenu();
    }
  };

  return (
    <>
      <MenuItem
        ref={mergeRefs(ref, itemRef, layerProps.triggerProps.ref)}
        role="menuitem"
        isFocused={isFocused}
        aria-expanded={isOpen}
        {...(isOpen && { 'aria-controls': menuId })}
        postfix={<ChevronDown className="size-4 -rotate-90 text-neutral-40" />}
        prefix={isDefined(Icon) ? <Icon className="size-4" /> : undefined}
        isDisabled={isDisabled}
        description={description}
        hasDivider={hasDivider}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
      >
        {label}
      </MenuItem>
      {isMobile ?
        <Portal>
          <DropdownDrawer title={label} isOpen={isOpen} isNested onBack={closeMenu} onClose={closeDropdown}>
            <DropdownMenu {...menu} id={menuId} onClose={closeMenu} />
          </DropdownDrawer>
        </Portal>
      : layerProps.renderLayer(
          <DropdownPopover
            isOpen={isOpen}
            style={layerProps.layerProps.style}
            ref={mergeRefs(layerProps.layerProps.ref, popoverRef)}
            onClose={closeMenu}
          >
            <DropdownMenu {...menu} id={menuId} onClose={closeMenu} />
          </DropdownPopover>,
        )
      }
    </>
  );
});
